import React, { useMemo, useState } from 'react';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { cn } from '@cotera/client/v0/lib/utils';
import type {
  WebhookProviderCatalogEntry,
  WebhookProviderCatalogEvent,
} from '../types';

const EventButton: React.FC<{
  event: WebhookProviderCatalogEvent;
  isSelected: boolean;
  onSelect: (eventKey: string) => void;
}> = ({ event, isSelected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(event.key)}
    className={cn(
      'rounded-md border p-3 text-left transition-colors',
      isSelected
        ? 'border-primary bg-primary/5'
        : 'border-border hover:bg-muted/40'
    )}
  >
    <Text.P className="font-medium leading-tight">{event.displayName}</Text.P>
    <Text.Caption className="text-muted-foreground mt-1 block">
      {event.description}
    </Text.Caption>
  </button>
);

/**
 * Ordered list of channel groups present in the catalog, preserving the
 * server's event order (first time a group appears wins its position).
 */
function orderedGroups(
  events: readonly WebhookProviderCatalogEvent[]
): string[] {
  const seen: string[] = [];
  for (const event of events) {
    if (event.group !== undefined && !seen.includes(event.group)) {
      seen.push(event.group);
    }
  }
  return seen;
}

function matchesQuery(
  event: WebhookProviderCatalogEvent,
  query: string
): boolean {
  const haystack =
    `${event.displayName} ${event.description} ${event.key}`.toLowerCase();
  return haystack.includes(query);
}

export const WebhookEventPicker: React.FC<{
  catalog: WebhookProviderCatalogEntry;
  selectedEventKey: string | null;
  onSelect: (eventKey: string) => void;
}> = ({ catalog, selectedEventKey, onSelect }) => {
  const events = catalog.events;
  const groups = useMemo(() => orderedGroups(events), [events]);
  const isGrouped = groups.length > 0;

  // The channel of the currently-selected event, so re-opening the editor lands
  // the user back on the right channel instead of the first one.
  const selectedEventGroup = useMemo(
    () => events.find((event) => event.key === selectedEventKey)?.group,
    [events, selectedEventKey]
  );
  const [activeGroup, setActiveGroup] = useState<string | null>(
    selectedEventGroup ?? groups[0] ?? null
  );
  const [query, setQuery] = useState('');

  if (events.length === 0) {
    return null;
  }

  // Ungrouped provider (the common case): keep the original flat grid.
  if (!isGrouped) {
    return (
      <div className="space-y-2">
        <Text.Caption className="text-muted-foreground block">
          Event
        </Text.Caption>
        <div className="grid gap-2">
          {events.map((event) => (
            <EventButton
              key={event.key}
              event={event}
              isSelected={event.key === selectedEventKey}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    );
  }

  const currentGroup = activeGroup ?? groups[0] ?? null;
  const normalizedQuery = query.trim().toLowerCase();
  // Search leads: a non-empty query filters across every channel. When empty,
  // the channel dropdown narrows the list.
  const isSearching = normalizedQuery !== '';
  const visibleEvents = isSearching
    ? events.filter((event) => matchesQuery(event, normalizedQuery))
    : events.filter((event) => event.group === currentGroup);

  return (
    <div className="space-y-2">
      <Text.Caption className="text-muted-foreground block">Event</Text.Caption>
      <div className="flex items-center gap-2">
        <Input
          type="search"
          placeholder="Search events across all channels…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1"
        />
        <Select
          value={currentGroup ?? undefined}
          onValueChange={(group) => setActiveGroup(group)}
        >
          <SelectTrigger className="w-40 shrink-0">
            <SelectValue placeholder="Channel" />
          </SelectTrigger>
          <SelectContent>
            {groups.map((group) => (
              <SelectItem key={group} value={group}>
                {group}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {visibleEvents.length === 0 ? (
        <Text.Caption className="text-muted-foreground block py-2">
          No events match “{query}”.
        </Text.Caption>
      ) : (
        <div className="grid gap-2">
          {visibleEvents.map((event) => (
            <EventButton
              key={event.key}
              event={event}
              isSelected={event.key === selectedEventKey}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};
